import { PublicLayout } from "@/components/PublicLayout";
import { SEOHead } from "@/components/SEOHead";
import { corrections } from "@/data/corrections";

function formatDate(iso: string) {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
}

const CorrectionsLogPage = () => {
  const entries = [...corrections].sort((a, b) => b.date.localeCompare(a.date));

  return (
    <PublicLayout>
      <SEOHead
        title="Corrections Log · David Isei"
        description="When a number changes, the change and the reason are published. A dated ledger of corrections to figures on Hawaiʻi healthcare workforce, network adequacy, and access."
        url="https://davidisei.com/corrections"
      />

      <section className="py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-liberty-gold">
            Corrections Log
          </p>
          <h1 className="mt-3 font-display text-4xl font-bold leading-tight text-foreground sm:text-5xl">
            Corrections Log
          </h1>
          <p className="mt-5 text-base leading-relaxed text-foreground/80">
            When a number changes, I publish the change and the reason. The
            ledger stays honest and the voice stays additive: look at the
            additional data, then we talk about it.
          </p>

          <ol className="mt-10 space-y-6">
            {entries.map((entry) => (
              <li
                key={entry.id}
                className="rounded-xl border border-border/60 bg-card/60 p-5 sm:p-6"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <time
                    dateTime={entry.date}
                    className="font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground"
                  >
                    {formatDate(entry.date)}
                  </time>
                  <span
                    className={`inline-flex items-center rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.12em] ${
                      entry.status === "published"
                        ? "border-liberty-gold/70 text-liberty-gold"
                        : "border-amber-500/70 text-amber-400"
                    }`}
                  >
                    {entry.status}
                  </span>
                </div>

                <h2 className="mt-3 font-display text-xl font-bold text-foreground">
                  {entry.title}
                </h2>

                <dl className="mt-4 space-y-3 text-sm leading-relaxed">
                  <div>
                    <dt className="font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                      Was
                    </dt>
                    <dd className="text-foreground/80">{entry.was}</dd>
                  </div>
                  <div>
                    <dt className="font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                      Now
                    </dt>
                    <dd className="text-foreground">{entry.now}</dd>
                    {entry.note && (
                      <dd className="mt-2 text-foreground/80">{entry.note}</dd>
                    )}
                  </div>
                  <div>
                    <dt className="font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                      Why
                    </dt>
                    <dd className="text-foreground/80">{entry.why}</dd>
                  </div>
                  <div>
                    <dt className="font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                      Where
                    </dt>
                    <dd className="text-foreground/80">{entry.where}</dd>
                  </div>
                </dl>
              </li>
            ))}
          </ol>
        </div>
      </section>
    </PublicLayout>
  );
};

export default CorrectionsLogPage;
